<template>
  <div class="container-fluid">
    <div class="row">
      <b-modal size="md" hide-footer ref="FinancesTaxTypesModal" centered :modal-class="isModalLoading ? 'loading' : ''">
        <template v-slot:modal-header="{ cancel }">
          <h5 class="modal-title">
            {{ !!editedItem.id ? "Edit tax type" : "Create tax type" }}
          </h5>
          <button type="button" aria-label="Close" class="close" @click.prevent="cancel">
            <span class="material-icons">
              close
            </span>
          </button>
        </template>
        <FinancesTaxTypesForm :dataProps="editedItem" :isLoading="isModalLoading" @submit="onSubmit" />
      </b-modal>

      <div class="col-12 full-height-page-wrapper">
        <div class="page-table-header">
          <PageHeader showBreadcrumbs page-title="Tax Types">
            <template #buttons>
              <b-button variant="success" class="button" @click="editEntityModalOpen()" :disabled="isLoading">
                Create
              </b-button>
            </template>
          </PageHeader>
        </div>

        <div v-if="isLoading && !isInitialDataLoaded" class="content-loading-wrapper" style="min-height: 60vh;">
          <Preloader class="centered-preloader large-spinner" />
        </div>

        <TableForPage v-else-if="taxTypes.length" :tableData="taxTypes" :fields="table.fields" :slots="table.slots" @edit="editEntityModalOpen"
          :isEdit="true" @remove="deleteTaxType" :isRemove="true" :isDataLoading="isLoading && isInitialDataLoaded" />

        <div v-else-if="isInitialDataLoaded" class="full-height-content">
          <EmptyStateNoResults
            customIcon="percent"
            customIconClasses="material-symbols-outlined empty-state__icon"
            title="No tax types added yet"
            description="Add tax types to ensure accurate tax calculation across your invoices and payouts."
            buttonText="CREATE"
            @button-click="handleCreateClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FinancesTaxTypesForm from '@components/forms/FinancesTaxTypesForm'
import TableForPage from '@tables/TableForPage'
import { mapGetters } from 'vuex'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults'

export default {
  name: 'FinancesTaxTypes',
  components: { TableForPage, FinancesTaxTypesForm, PageHeader, Preloader, EmptyStateNoResults },
  data () {
    return {
      isLoading: false,
      isModalLoading: false,
      isOpenDialog: false,
      editedItem: {},
      isAllOptionsFilterSet: false,
      isTaxTypesDataLoaded: false,
      table: {
        data: [],
        fields: [
          {
            key: 'tax_type_name',
            label: 'Name',
            sortable: true
          },
          {
            key: 'formula_percent',
            label: 'Formula percent'
          },
          {
            key: 'formula_fixed',
            label: 'Formula fixed'
          },
          {
            key: 'currency.code',
            label: 'Currency'
          },
          {
            key: 'comment',
            label: 'Comment'
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'column_action'
          }
        ],
        slot: []
      }
    }
  },
  computed: {
    isInitialDataLoaded () {
      return this.isAllOptionsFilterSet && this.isTaxTypesDataLoaded
    },
    ...mapGetters({
      taxTypes: 'finances/getTaxTypes'
    })
  },

  async mounted () {
    await this.fetchTaxTypes()
    this.isAllOptionsFilterSet = true
  },

  methods: {
    async fetchTaxTypes () {
      this.isLoading = true
      await this._toggleDataPageLoading(() => this.$api.finances.getTaxTypes())
      this.isLoading = false
      this.isTaxTypesDataLoaded = true
    },
    editEntityModalOpen (item) {
      if (item) {
        this.editedItem = item
      } else {
        this.editedItem = {}
      }

      this.isOpenDialog = true
      this.$refs['FinancesTaxTypesModal'].show()
    },

    async onSubmit (data) {
      if (this.editedItem.id) {
        await this.updateTaxType({ ...data, id: this.editedItem.id })
      } else {
        await this.createTaxType(data)
      }
      this.fetchTaxTypes()
    },

    async createTaxType (data) {
      this.isModalLoading = true

      return this.axios
        .post(
          '/api/finances/tax-types',
          data,
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          }
        )
        .then(({ data }) => {
          this.$store.dispatch('finances/addTaxType', data)

          this.$snotify.success(
            'Tax type successfully created',
            'Success',
            this.$store.state.snotify
          )

          this.isOpenDialog = false
          this.editedItem = {}
          this.$refs['FinancesTaxTypesModal'].hide()
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    async updateTaxType (data) {
      this.isModalLoading = true

      return this.axios
        .put(
          `/api/finances/tax-types/${this.editedItem.id}`,
          data,
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          }
        )
        .then(({ data }) => {
          this.$store.dispatch('finances/editTaxType', data)

          this.$snotify.success(
            'Tax type successfully updated',
            'Success',
            this.$store.state.snotify
          )

          this.isOpenDialog = false
          this.editedItem = {}
          this.$refs['FinancesTaxTypesModal'].hide()
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isModalLoading = false
        })
    },
    async deleteTaxType (item) {
      this.isLoading = true

      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete?'
      )

      if (!confirm) {
        this.isLoading = false
        return
      }

      this.axios
        .delete(
          `/api/finances/tax-types/${item.id}`,
          {
            headers: {
              Authorization: 'Bearer' + localStorage.getItem('access_token')
            }
          }
        )
        .then(({ data }) => {
          this.$store.dispatch('finances/removeTaxType', parseInt(data.id))
          this.$snotify.success(
            'Tax type successfully deleted',
            'Success',
            this.$store.state.snotify
          )

          this.editedItem = {}
        })
        .catch(error => {
          this.errorNotifications(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    handleCreateClick () {
      this.editEntityModalOpen()
    }
  }
}
</script>
